// Every rental is handed over in Georgia, so a pick-up or return time on the
// site means GEORGIA's clock — wherever the customer's laptop happens to be.
//
// Browsers only speak their own local time: a date picker hands back "10:00"
// as 10:00 in the visitor's zone. Taken at face value, a customer in Berlin who
// chose a 10:00 pick-up at Tbilisi airport was recorded as 10:00 Berlin time,
// and the desk saw 13:00. These helpers translate between "the wall clock in
// Georgia" and real instants, so the hour picked is the hour the desk sees.
//
// Georgia is UTC+4 all year (no daylight saving since 2005), so a fixed offset
// is exact. Plain JS — imported by client components and by lib/pricing alike.

export const GEORGIA_TZ = "Asia/Tbilisi";
export const GEORGIA_OFFSET_MS = 4 * 3600 * 1000;
const OFFSET_S = GEORGIA_OFFSET_MS / 1000;

/** Unix seconds of a wall-clock moment in Georgia (`monthIndex` is 0-based). */
export const georgiaTs = (year, monthIndex, day, hour = 0, minute = 0) =>
    Math.floor(Date.UTC(year, monthIndex, day, hour, minute) / 1000) - OFFSET_S;

/** Georgia's wall clock at `ts` (unix seconds), as plain fields. */
export const georgiaParts = (ts) => {
    const d = new Date((Number(ts) || 0) * 1000 + GEORGIA_OFFSET_MS);
    return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth(),
        day: d.getUTCDate(),
        hour: d.getUTCHours(),
        minute: d.getUTCMinutes(),
    };
};

/** A Date whose LOCAL fields read as Georgia's wall clock at `ts` — what a date
 *  picker has to be given to show Georgia time. Not a real instant. */
export const georgiaWallAsLocal = (ts) => {
    const p = georgiaParts(ts);
    return new Date(p.year, p.month, p.day, p.hour, p.minute, 0, 0);
};

/** The reverse: a picker's Date, whose local fields ARE Georgia's wall clock,
 *  as real unix seconds. */
export const tsFromGeorgiaWall = (date) =>
    georgiaTs(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());

/** Same calendar day in Georgia? (unix seconds) */
export const sameGeorgiaDay = (a, b) => {
    const x = georgiaParts(a);
    const y = georgiaParts(b);
    return x.year === y.year && x.month === y.month && x.day === y.day;
};

/** Is this device's clock on a different offset than Georgia's right now? Only
 *  then is it worth telling the visitor which clock the times are on. */
export const clockDiffersFromGeorgia = () => new Date().getTimezoneOffset() * 60000 !== -GEORGIA_OFFSET_MS;
